import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useT, useLang, itemHasLocale } from '../lib/i18n';
import { BACKEND_URL as API } from '../lib/config';

// ─────────────────────────────────────────────────────────────────────────
// ModernNews — the «Moderno» theme "Latest News" (s139, approved by Anthony:
// N1 "Lead + List"). The newest post big with image and summary; the next
// ones as a list (category · date · title). Only the lead needs an image
// (missing/broken → brand gradient). Header + "View all" = CMS → Section
// Manager → Latest News — Section Configuration. Colours from theme tokens.
// ─────────────────────────────────────────────────────────────────────────

const SERIF = "'Playfair Display', Georgia, serif";
const img = (src) => (src && src.startsWith('/api') ? `${API}${src}` : src);
const plain = (html) => String(html || '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;| /g, ' ').replace(/\s+/g, ' ').trim();
const fmt = (d) => { const x = new Date(d); return isNaN(x) ? '' : x.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); };

function Meta({ p }) {
  const tt = useT();
  const cat = tt(p.category);
  const date = fmt(p.created_at);
  return <span className="text-[11px] font-bold uppercase" style={{ letterSpacing: '.12em', color: 'var(--accent-strong, #b86500)' }}>{[cat, date].filter(Boolean).join(' · ')}</span>;
}

function LeadImage({ src }) {
  const [broken, setBroken] = React.useState(false);
  const url = img(src);
  if (!url || broken) return <span className="block w-full h-full" style={{ background: 'linear-gradient(150deg, color-mix(in srgb, var(--color-accent, #ff9c1b) 35%, #fff), var(--color-accent, #ff9c1b))' }} />;
  return <img src={url} alt="" onError={() => setBroken(true)} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />;
}

export default function ModernNews({ posts, cmsConfig = {} }) {
  const tt = useT();
  const { lang } = useLang();
  const list = (posts || []).filter(p => itemHasLocale(p.title, lang)).slice(0, 4);
  if (!list.length) return null;
  const [lead, ...rest] = list;
  const href = (p) => `/news/${p.slug || p.id}`;

  const title = tt(cmsConfig.title) || 'Latest News';
  const words = title.trim().split(/\s+/);
  const last = words.length > 1 ? words.pop() : null;
  const ctaText = tt(cmsConfig.cta_text);
  const ctaUrl = tt(cmsConfig.cta_url) || '/news';
  const summary = plain(tt(lead.summary) || tt(lead.excerpt) || tt(lead.content)).slice(0, 180);

  return (
    <section id="news" data-testid="news-section" className="bg-white">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-[clamp(24px,5vw,80px)] py-20 lg:py-28">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 lg:mb-14">
          <div className="flex flex-col gap-4 max-w-2xl">
            {tt(cmsConfig.eyebrow) && (
              <div className="inline-flex items-center gap-3 text-[11px] font-bold uppercase" style={{ letterSpacing: '.2em', color: 'var(--accent-strong, #b86500)' }}>
                <span style={{ width: 32, height: 2, background: 'var(--color-accent, #ff9c1b)' }} />{tt(cmsConfig.eyebrow)}
              </div>
            )}
            <h2 className="font-bold" style={{ fontFamily: SERIF, fontSize: 'clamp(2rem, 3.4vw, 3rem)', lineHeight: 1.08, color: 'var(--color-heading, #1a2332)' }} data-testid="news-title">
              {words.join(' ')}{last && <> <em style={{ fontStyle: 'italic', fontWeight: 500, color: 'var(--accent-strong, #b86500)' }}>{last}</em></>}
            </h2>
            {tt(cmsConfig.subtitle) && <p className="text-base md:text-lg leading-relaxed" style={{ color: 'var(--color-body-text, #475569)' }}>{tt(cmsConfig.subtitle)}</p>}
          </div>
          <Link to={ctaUrl} className="mx-link inline-flex items-center gap-2 font-bold text-sm self-start md:self-end" style={{ color: 'var(--accent-strong, #b86500)' }} data-testid="news-view-all">
            {ctaText || 'View all news'} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className={`grid gap-10 lg:gap-14 items-start ${rest.length ? 'lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]' : ''}`}>
          <Link to={href(lead)} className="group flex flex-col gap-4" data-testid={`news-lead-${lead.id}`}>
            <div className="aspect-[16/9] overflow-hidden rounded-[3px]"><LeadImage src={lead.image} /></div>
            <Meta p={lead} />
            <h3 className="font-bold transition-colors duration-200 group-hover:text-[color:var(--accent-strong)]" style={{ fontFamily: SERIF, fontSize: 'clamp(1.5rem, 2.2vw, 2rem)', lineHeight: 1.2, color: 'var(--color-heading, #1a2332)' }}>{tt(lead.title)}</h3>
            {summary && <p className="text-base leading-relaxed" style={{ color: 'var(--color-body-text, #475569)' }}>{summary}{summary.length >= 180 ? '…' : ''}</p>}
          </Link>
          {rest.length > 0 && (
            <ul className="m-0 p-0 list-none border-t" style={{ borderColor: 'rgba(20,26,58,.1)' }}>
              {rest.map(p => (
                <li key={p.id} className="border-b" style={{ borderColor: 'rgba(20,26,58,.1)' }}>
                  <Link to={href(p)} className="group flex items-start justify-between gap-4 py-6" data-testid={`news-item-${p.id}`}>
                    <span className="flex flex-col gap-2">
                      <Meta p={p} />
                      <span className="font-semibold transition-colors duration-200 group-hover:text-[color:var(--accent-strong)]" style={{ fontFamily: SERIF, fontSize: '1.3rem', lineHeight: 1.25, color: 'var(--color-heading, #1a2332)' }}>{tt(p.title)}</span>
                    </span>
                    <ArrowRight className="w-5 h-5 flex-none mt-6" style={{ color: 'var(--accent-strong, #b86500)' }} />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}
